import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface MaintenanceMode {
  enabled: boolean
  message?: string
  allow_preview?: boolean
}

export interface HeroSettings {
  background_image?: string
  background_opacity?: number
  logo_image?: string
  logo_link?: string
  title?: string
  title_highlight?: string
  description?: string
  button1_text?: string
  button2_text?: string
}

interface SiteSettings {
  maintenance_mode: MaintenanceMode
  hero_settings?: HeroSettings
  contact_info?: {
    company_name: string
    address: string
    city: string
    country: string
    phone: string
    email: string
  }
  social_links?: {
    linkedin?: string
    facebook?: string
    twitter?: string
    instagram?: string
  }
}

// Check if preview mode is enabled via URL parameter
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('preview') === 'true'
}

// Hook to get maintenance mode status
export function useMaintenanceMode() {
  return useQuery({
    queryKey: ['maintenance-mode'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .single()

      if (error) {
        // Default to false if setting doesn't exist
        console.warn('Maintenance mode setting not found:', error)
        return { enabled: false, message: '', allow_preview: true } as MaintenanceMode
      }

      return data.value as MaintenanceMode
    },
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  })
}

// Hook to get all site settings
export function useSiteSettings() {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')

      if (error) {
        console.error('Failed to load site settings:', error)
        return {} as SiteSettings
      }

      // Convert array to object
      const settings: Partial<SiteSettings> = {}
      data.forEach((item) => {
        ;(settings as Record<string, unknown>)[item.key] = item.value
      })

      return settings as SiteSettings
    },
    staleTime: 60000, // Cache for 1 minute
  })
}

// Hook to update maintenance mode
export function useUpdateMaintenanceMode() {
  const updateMaintenanceMode = async (settings: MaintenanceMode) => {
    const { error } = await supabase
      .from('site_settings')
      .update({ value: settings })
      .eq('key', 'maintenance_mode')

    if (error) throw error
    return true
  }

  return { updateMaintenanceMode }
}

// Hook to get hero settings
export function useHeroSettings() {
  return useQuery({
    queryKey: ['hero-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'hero_settings')
        .single()

      if (error) {
        // Default settings if not found
        return { background_image: '', background_opacity: 30 } as HeroSettings
      }

      return data.value as HeroSettings
    },
    staleTime: 60000,
  })
}
